import { VariantProps } from '@stitches/react';
import { ChangeEvent, memo, ReactNode } from 'react';
import { useId } from '../../../utils/useId';
import {
  InvalidMessage,
  Label,
  TextboxMultiline,
  TextboxSingleline,
} from './Textbox.styles';

type TextboxCoreProps = {
  multiline?: boolean;
  fieldId: string;
  onBlur?: () => void;
  onChange?: (
    eventArgs: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  value?: string;
  required?: boolean;
  type?: 'text' | 'email' | 'tel' | 'number' | 'password';
  registrationFunction?: (value: string) => Object;
};

type TextboxProps = {
  invalid?: boolean;
  invalidMessage?: ReactNode;
  label?: ReactNode;
} & TextboxCoreProps;

function TextboxSelect({
  id,
  multiline,
  fieldId,
  onBlur,
  onChange,
  validation,
  value,
  required,
  type = 'text',
  registrationFunction = () => ({}),
}: TextboxCoreProps & {
  id: string;
  validation?: VariantProps<
    typeof TextboxSingleline | typeof TextboxSingleline
  >['validation'];
}) {
  if (multiline) {
    return (
      <TextboxMultiline
        id={id}
        name={fieldId}
        required={required}
        {...registrationFunction(fieldId)}
      />
    );
  }

  return (
    <TextboxSingleline
      id={id}
      name={fieldId}
      required={required}
      type={type}
      {...registrationFunction(fieldId)}
    />
  );
}

function TextboxPureComponent({
  invalid,
  invalidMessage,
  label,
  multiline,
  fieldId,
  onBlur,
  onChange,
  value,
  required,
  type,
  registrationFunction,
}: TextboxProps) {
  const id = useId();

  return (
    <>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <TextboxSelect
        id={id}
        multiline={multiline}
        fieldId={fieldId}
        onBlur={onBlur}
        onChange={onChange}
        validation={invalid ? 'invalid' : undefined}
        value={value}
        required={required}
        type={type}
        registrationFunction={registrationFunction}
      />
      {invalidMessage ? (
        <InvalidMessage aria-live="polite">{invalidMessage}</InvalidMessage>
      ) : null}
    </>
  );
}

export const Textbox = memo(TextboxPureComponent);
